import React, { useEffect, useState } from "react";
import { api, formatMoney, CURRENCIES } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";

const CATEGORIES = ["electricity", "water", "gas", "internet", "hoa", "mortgage", "insurance", "repairs", "other"];

const today = () => new Date().toISOString().slice(0, 10);
const empty = {
  property_id: "", category: "electricity", amount: 0, currency: "TRY", rate_to_try: 1,
  date: today(), vendor: "", notes: "", insurance_start: "", insurance_end: "",
};

export default function Expenses() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const [es, ps] = await Promise.all([api.get("/expenses"), api.get("/properties")]);
    setItems(es.data); setProps(ps.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      amount: parseFloat(form.amount) || 0,
      rate_to_try: parseFloat(form.rate_to_try) || 1,
      property_id: form.property_id || null,
    };
    try {
      if (editingId) await api.put(`/expenses/${editingId}`, payload);
      else await api.post("/expenses", payload);
      toast.success("OK");
      setOpen(false); setForm(empty); setEditingId(null); load();
    } catch (e) { toast.error("Error"); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/expenses/${id}`);
    load();
  };

  const edit = (x) => {
    setForm({ ...empty, ...x, property_id: x.property_id || "" });
    setEditingId(x.id);
    setOpen(true);
  };

  const propName = (id) => props.find((p) => p.id === id)?.name || "-";

  // YTD totals in TRY (using rate_to_try)
  const y = new Date().getFullYear();
  const totalYtd = items.reduce((s, x) => {
    try {
      const d = new Date(x.date);
      if (d.getFullYear() === y) return s + (x.amount || 0) * (x.rate_to_try || 1);
    } catch {}
    return s;
  }, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-eyebrow">{t("expenses")}</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-slate-900">{t("expenses")}</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-expense-btn" className="bg-slate-900 hover:bg-slate-800 rounded-md">
              <Plus size={16} className="mr-2" /> {t("add")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? t("edit") : t("add")}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-3">
              <div><label className="label-eyebrow">{t("category")}</label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(c)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><label className="label-eyebrow">{t("property")}</label>
                <Select value={form.property_id || "none"} onValueChange={(v) => setForm({ ...form, property_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="expense-property"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("all_properties")}</SelectItem>
                    {props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><label className="label-eyebrow">{t("amount")}</label>
                <Input data-testid="expense-amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("currency")}</label>
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger data-testid="expense-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.currency !== "TRY" && (
                <div className="col-span-2">
                  <div className="flex items-center justify-between">
                    <label className="label-eyebrow">{t("rate")}</label>
                    <button type="button" data-testid="fetch-rate-exp" onClick={async () => {
                      try {
                        const { data } = await api.get("/fx/rates");
                        const r = data[form.currency];
                        if (r) { setForm({ ...form, rate_to_try: r }); toast.success(`${t("fetch_rate_ok")}: ${r}`); }
                        else toast.error(t("fetch_rate_err"));
                      } catch { toast.error(t("fetch_rate_err")); }
                    }} className="text-xs uppercase tracking-widest font-bold text-slate-500 hover:text-slate-900 border border-slate-200 px-2 py-0.5 rounded">
                      {t("fetch_rate")}
                    </button>
                  </div>
                  <Input data-testid="expense-rate" type="number" step="0.0001" value={form.rate_to_try} onChange={(e) => setForm({ ...form, rate_to_try: e.target.value })} />
                </div>
              )}
              <div><label className="label-eyebrow">{t("date")}</label>
                <Input data-testid="expense-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div><label className="label-eyebrow">{t("vendor")}</label>
                <Input data-testid="expense-vendor" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></div>

              {form.category === "insurance" && (
                <>
                  <div><label className="label-eyebrow">{t("insurance_start")}</label>
                    <Input data-testid="expense-ins-start" type="date" value={form.insurance_start} onChange={(e) => setForm({ ...form, insurance_start: e.target.value })} /></div>
                  <div><label className="label-eyebrow">{t("insurance_end")}</label>
                    <Input data-testid="expense-ins-end" type="date" value={form.insurance_end} onChange={(e) => setForm({ ...form, insurance_end: e.target.value })} /></div>
                </>
              )}

              <div className="col-span-2"><label className="label-eyebrow">{t("notes")}</label>
                <Textarea data-testid="expense-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <DialogFooter className="col-span-2"><Button data-testid="expense-save" type="submit" className="bg-slate-900 hover:bg-slate-800">{t("save")}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="label-eyebrow">{t("total_expense")} (TRY)</div>
        <div className="font-heading text-3xl font-semibold mt-2 text-red-600">{formatMoney(totalYtd, "TRY")}</div>
      </div>

      {(() => {
        const insurancesByProp = {};
        items.filter((x) => x.category === "insurance").forEach((x) => {
          const key = x.property_id || "_none";
          if (!insurancesByProp[key]) insurancesByProp[key] = [];
          insurancesByProp[key].push(x);
        });
        const propIdsWithInsurance = Object.keys(insurancesByProp);
        if (propIdsWithInsurance.length === 0) return null;
        return (
          <section className="bg-white border border-slate-200 rounded-lg p-6" data-testid="property-insurance-summary">
            <div className="flex items-center gap-2 mb-4">
              <div className="label-eyebrow">{t("insurance_info")}</div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {propIdsWithInsurance.map((pid) => (
                <div key={pid} className="border border-slate-200 rounded-md p-4" data-testid={`prop-ins-${pid}`}>
                  <div className="font-heading text-base font-medium text-slate-900">{propName(pid)}</div>
                  <div className="mt-2 space-y-1.5">
                    {insurancesByProp[pid].sort((a, b) => (b.insurance_end || "").localeCompare(a.insurance_end || "")).slice(0, 3).map((ins) => {
                      const today = new Date().toISOString().slice(0, 10);
                      const expired = ins.insurance_end && ins.insurance_end < today;
                      return (
                        <div key={ins.id} className="text-xs flex items-center justify-between">
                          <span className="text-slate-600 truncate mr-2">{ins.vendor || t("insurance")}</span>
                          <span className={`font-semibold ${expired ? "text-red-600" : "text-slate-900"}`}>
                            {ins.insurance_start || "?"} → {ins.insurance_end || "?"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })()}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 label-eyebrow">{t("date")}</th>
              <th className="text-left px-4 py-3 label-eyebrow">{t("category")}</th>
              <th className="text-left px-4 py-3 label-eyebrow">{t("property")}</th>
              <th className="text-left px-4 py-3 label-eyebrow">{t("vendor")}</th>
              <th className="text-right px-4 py-3 label-eyebrow">{t("amount")}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">{t("no_data")}</td></tr>}
            {items.map((x) => (
              <tr key={x.id} data-testid={`exp-row-${x.id}`}>
                <td className="px-4 py-3">
                  {x.date}
                  {x.category === "insurance" && x.insurance_end && (
                    <div className="text-[10px] uppercase tracking-widest text-amber-600 font-bold mt-1">
                      {t("insurance_end")}: {x.insurance_end}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 font-medium">{t(x.category)}</td>
                <td className="px-4 py-3 text-slate-600">{propName(x.property_id)}</td>
                <td className="px-4 py-3 text-slate-600">{x.vendor || "-"}</td>
                <td className="px-4 py-3 text-right font-semibold text-red-600">{formatMoney(x.amount, x.currency)}</td>
                <td className="px-4 py-3 text-right space-x-1">
                  <button onClick={() => edit(x)} className="p-2 hover:bg-slate-100 rounded" data-testid={`exp-edit-${x.id}`}>
                    <PencilSimple size={16} />
                  </button>
                  <button onClick={() => del(x.id)} className="p-2 hover:bg-red-50 text-red-600 rounded" data-testid={`exp-del-${x.id}`}>
                    <Trash size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
